import React, { useEffect, useContext } from "react";
import classes from "../Store.module.scss";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import useFetch from "../../../../hooks/useFetch";
import MenuItem from "@mui/material/MenuItem";
import { useTranslation } from "react-i18next";
import { useFormValidation } from "../hooks/useFormValidation";
import { API_GET_USER_INVOICE_INFO } from "../../../../constants/api";
import sessionContext from '../../../../context/sessionContext';
import Spinner from "../../../../components/Spinner";

interface InvoiceFormProps {
  setUserInfo: React.Dispatch<React.SetStateAction<any>>;
  userInfo: {
    name: string;
    professional_ssn: string;
    street: string;
    number: string;
    complement?: string;
    cep: string;
    neighborhood: string;
    city: string;
    state: string;
    country: string;
  };
  setUpdatedUserNFInfo: React.Dispatch<React.SetStateAction<boolean>>;
  setIsUserOldClient: React.Dispatch<React.SetStateAction<boolean>>;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ setUserInfo, userInfo, setUpdatedUserNFInfo, setIsUserOldClient }) => {
  const { t } = useTranslation();
  const cSession = useContext(sessionContext);

  const [NFuserInfo, isLoadingNFuserInfo, errorNFuserInfo, getNFuserInfo]: any = useFetch({
    autoInit: false,
  });
  // NFuserInfo deve ser enviado ao componente pai que chama InvoiceForm
  console.log("NFuserInfo", NFuserInfo);
  const adminInfo = cSession.admin;
  useEffect(() => {
    getNFuserInfo({
      method: "GET",
      url: API_GET_USER_INVOICE_INFO,
      onSuccess: (data: any) => {
        // NFuserInfo deve ser enviado ao componente pai que chama InvoiceForm
        if (NFuserInfo) setIsUserOldClient(true);
      },
      onError: (error: any) => {
        console.error('Erro ao buscar informações do usuário:', errorNFuserInfo);
      }
    });
  }, []);

  useEffect(() => {
    //caso NFuserInfo exista, pré-preencher as informações do usuário no formulário
    if (NFuserInfo) {
      setUserInfo({
        customer_id: NFuserInfo.id || "",
        name: NFuserInfo.name || "",
        professional_ssn: NFuserInfo.registry_code || "",
        street: NFuserInfo.address?.street || "",
        number: NFuserInfo.address?.number || "",
        complement: NFuserInfo.address?.additional_details || "",
        cep: NFuserInfo.address?.zipcode || "",
        neighborhood: NFuserInfo.address?.neighborhood || "",
        city: NFuserInfo.address?.city || "",
        state: NFuserInfo.address?.state || "",
        country: NFuserInfo.address?.country || "",
      });
    }
    setUpdatedUserNFInfo(false) // resetar o estado de atualização das informações do usuário
  }, [NFuserInfo])


  const {
    fieldsTouched,
    errosInput,
    handleBlur,
    formatDocumentNumber,
    formatCep,
    fetchAddress,
    numeroRef,
  } = useFormValidation(setUserInfo);

  const MenuItemBRStates = () => {
    const states = [
      "AC",
      "AL",
      "AP",
      "AM",
      "BA",
      "CE",
      "DF",
      "ES",
      "GO",
      "MA",
      "MT",
      "MS",
      "MG",
      "PA",
      "PB",
      "PR",
      "PE",
      "PI",
      "RJ",
      "RN",
      "RS",
      "RO",
      "RR",
      "SC",
      "SP",
      "SE",
      "TO",
    ];
    const itens = states.map((state) => (
      <MenuItem key={String(state)} value={String(state)}>
        {String(state)}
      </MenuItem>
    ));
    return itens;
  };

  return (
    isLoadingNFuserInfo ? <Spinner /> :
    <div className={classes.CardInvoiceInfo}>
      <h2>{t("global.invoice_data")}</h2>
      <div className={classes.InputsArea}>
        <TextField
          className={classes.TextField}
          id="name"
          value={userInfo.name || ""}
          autoFocus
          required
          inputProps={{ maxLength: 150 }}
          label={t("global.full_name")}
          type="text"
          error={fieldsTouched.name && errosInput.name}
          helperText={
            fieldsTouched.name && errosInput.name
              ? t("purchase.the_name_provided_is_not_valid")
              : ""
          }
          onBlur={(e) => handleBlur("name", e.target.value)}
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
        <TextField
          className={classes.TextField}
          autoFocus
          id="professional_ssn"
          value={userInfo.professional_ssn || ""}
          required
          inputProps={{ maxLength: 18 }}
          label={t("global.cpf_cnpj")}
          type="text"
          error={fieldsTouched.professional_ssn && errosInput.professional_ssn}
          helperText={
            fieldsTouched.professional_ssn && errosInput.professional_ssn
              ? t("purchase.the_cpf_or_cnpj_is_not_valid")
              : ""
          }
          onChange={(e) => {
            setUserInfo({
              ...userInfo,
              professional_ssn: formatDocumentNumber(e.target.value),
            });
            setUpdatedUserNFInfo(true);
            handleBlur("professional_ssn", e.target.value);
          }}
        />
        <FormControl
          className={classes.TextField}
          error={fieldsTouched.country && errosInput.country}
        >
          <Select
            className={classes.TextField}
            id="country"
            value={userInfo.country || ""}
            displayEmpty
            required
            type="text"
            error={fieldsTouched.country && errosInput.country}
            onBlur={(e) => handleBlur("country", e.target.value)}
            onChange={(e) => {
              setUserInfo({ ...userInfo, country: e.target.value });
              setUpdatedUserNFInfo(true);
            }}
          >
            <MenuItem value={""} disabled>País</MenuItem>
            <MenuItem value={"BR"}>Brasil</MenuItem>
          </Select>

          <FormHelperText>
            {fieldsTouched.country && errosInput.country
              ? t("purchase.please_select_a_country_to_proceed")
              : ""}
          </FormHelperText>
        </FormControl>
        <TextField
          className={classes.TextField}
          autoFocus
          id="cep"
          value={userInfo.cep || ""}
          inputProps={{ maxLength: 9 }}
          required
          label={t("global.cep")}
          type="text"
          error={fieldsTouched.cep && errosInput.cep}
          helperText={
            fieldsTouched.cep && errosInput.cep
              ? t("purchase.please_provide_a_valid_postal_code")
              : ""
          }
          onChange={(e) => {
            setUserInfo({
              ...userInfo,
              cep: formatCep(e.target.value),
            });
            setUpdatedUserNFInfo(true);
            handleBlur("cep", e.target.value);
            fetchAddress(e.target.value);
          }}
        />
        <FormControl
          className={classes.TextField}
          error={fieldsTouched.state && errosInput.state}
        >
          <Select
            className={classes.TextField}
            id="state"
            value={userInfo.state || ""}
            displayEmpty
            required
            onBlur={(e) => handleBlur("state", e.target.value)}
            onChange={(e) => {
              setUserInfo({ ...userInfo, state: e.target.value });
              setUpdatedUserNFInfo(true);
            }
            }
          >
            <MenuItem value="">Estado</MenuItem>
            {MenuItemBRStates()}
          </Select>
          <FormHelperText>
            {fieldsTouched.state && errosInput.state
              ? t("purchase.please_select_a_state_to_proceed")
              : ""}
          </FormHelperText>
        </FormControl>
        <TextField
          className={classes.TextField}
          autoFocus
          id="city"
          required
          inputProps={{ maxLength: 60 }}
          value={userInfo.city || ""}
          label={t("global.city")}
          type="text"
          error={fieldsTouched.city && errosInput.city}
          helperText={
            fieldsTouched.city && errosInput.city
              ? "Por favor, verifique se a cidade informada é válida"
              : ""
          }
          onBlur={(e) => handleBlur("city", e.target.value)}
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
        <TextField
          className={classes.TextField}
          autoFocus
          id="neighborhood"
          inputProps={{ maxLength: 60 }}
          value={userInfo.neighborhood || ""}
          required
          label={t("global.neighborhood")}
          type="text"
          error={fieldsTouched.neighborhood && errosInput.neighborhood}
          helperText={
            fieldsTouched.neighborhood && errosInput.neighborhood
              ? "O campo Bairro não pode permanecer vazio"
              : ""
          }
          onBlur={(e) => handleBlur("neighborhood", e.target.value)}
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
        <TextField
          className={classes.TextField}
          autoFocus
          id="street"
          value={userInfo.street || ""}
          inputProps={{ maxLength: 100 }}
          required
          label={t("global.street")}
          type="text"
          error={fieldsTouched.street && errosInput.street}
          helperText={
            fieldsTouched.street && errosInput.street
              ? "O campo Rua não pode permanecer vazio"
              : ""
          }
          onBlur={(e) => handleBlur("street", e.target.value)}
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
        <TextField
          inputRef={numeroRef}
          className={classes.TextField}
          autoFocus
          id="number"
          inputProps={{ maxLength: 10 }}
          value={userInfo.number || ""}
          required
          label={t("global.number")}
          type="text"
          error={fieldsTouched.number && errosInput.number}
          helperText={
            fieldsTouched.number && errosInput.number
              ? "O campo Número não pode permanecer vazio"
              : ""
          }
          onBlur={(e) => handleBlur("number", e.target.value)}
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
        <TextField
          className={classes.TextField}
          autoFocus
          id="complement"
          label={t("global.complement")}
          type="text"
          onChange={(e) => {
            setUserInfo({ ...userInfo, [e.target.id]: e.target.value });
            setUpdatedUserNFInfo(true);
          }}
        />
      </div>
    </div>
  );
};

export default InvoiceForm;
